import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getUserInfoThunk, userSliceSelector } from "../../features/userSlice";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useEffect, useRef, useState } from "react";
import { getQuotationsThunk, quotationsSelector } from "../../features/quotationsSlice";
import { DataTable } from "primereact/datatable";
import { inspectionDateColumn, vesselColumn, vesselTypeColumn } from "../shared-columns/shared-columns";
import { QuotationEnquiriesTableFields } from "./Fields";
import { MultiSelectTemplate, RowWithSubtext, successToast, vesselBodyTemplate } from "@idwal/idwal-react-components";
import { Column } from "primereact/column";
import { getInspectionDate } from "../../utils/dates";
import { QuotationEnquiriesTableRow } from "../../types/quotations/QuotationEnquiriesTableRow";
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../../constants/TableConstants";
import { defaultEnquiriesFiltersConfig, enquiriesTableGlobalFilterFields, quotationDateFilterOptions } from "../table-header/FilterSetup";
import { QuotationStatus } from "../../constants/QuotationConstants";
import { useNavigate } from "react-router-dom";
import { QUOTATIONS_ROUTE } from "../../config/router";
import { useDebounceCallback } from "usehooks-ts";
import TableHeader from "../table-header/TableHeader";
import { InspectionsTableFields } from "../inspections-table/Fields";
import { QuoteTableActionsBody } from "../inspections-table/QuoteActionsBody";
import { downloadFile } from "../../utils/downloadFile";

const ENQUIRIES_TABLE_KEY = "enquiriesTable";


export const EnquiriesTable = () => {
    const { t } = useTranslation("locale");
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);

    const {Quotations, QuotationsStatus} = useAppSelector(quotationsSelector);
    const {
        UserInfoStatus,
        SelectedGroupName,
        OrganisationsInfo,
        OrganisationsInfoStatus
    } = useAppSelector(userSliceSelector);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [first, setFirst] = useState<number>(0);
    const [rows, setRows] = useState(DEFAULT_ROWS_PER_PAGE);

    const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
    const enquiriesTableRef = useRef(null);
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
    const debounced = useDebounceCallback((value: string) => {
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
    }, 300);

    const onPage = (event: any) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const [filters, setFilters] = useState(defaultEnquiriesFiltersConfig());

    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;

        setDebouncedSearchValue(value);
        debounced.cancel();
        debounced(value);
    }

    const clearFilters = () => {
        debounced.cancel();
        setFilters(defaultEnquiriesFiltersConfig());
    }

    useEffect(() => {
        const requestsSuccessful = UserInfoStatus === "succeeded" && OrganisationsInfoStatus === "succeeded";

        if (!requestsSuccessful) {
            return;
        }

        const selectedGroup = SelectedGroupName !== "" ? SelectedGroupName : OrganisationsInfo[0].id.toString();
        const quotationsForSelectedOrg = Quotations?.OrgIds.includes(selectedGroup);
        if (requestsSuccessful && (!quotationsForSelectedOrg || Quotations === null))
        {
            dispatch(getQuotationsThunk({
                orgIds: [selectedGroup],
                user: user,
                statuses: [QuotationStatus.LEAD, QuotationStatus.OFFER]
            }));
        }
    }, [dispatch, UserInfoStatus, SelectedGroupName, OrganisationsInfo]);

    useEffect(() => {
        if (user && UserInfoStatus === "idle") {
            dispatch(getUserInfoThunk({userId: user.username!}));
        }
    }, [user]);

    const getTotalPriceDisplay = (totalPrice: number, status: number) => {
        if (status === QuotationStatus.LEAD) {
            return t("quotations.enquiries.table.pending");
        }

        return `$${totalPrice.toString()}`;
    }

    const tableRows: QuotationEnquiriesTableRow[] = Quotations !== null ? Quotations.Quotations.map((quotation) => ({
        vesselName: quotation.vesselName,
        vesselNameFilterField:
            quotation.vesselName + " " + quotation.vesselImo,
        imo: quotation.vesselImo,
        inspectionType: quotation.inspectionType,
        quotationNumber: quotation.quotationNumber,
        totalPrice: getTotalPriceDisplay(quotation.totalPrice, quotation.status),
        totalPriceSortField: quotation.totalPrice ?? undefined,
        status: quotation.status,
        statusName: quotation.statusName,
        vesselType: quotation.vesselType,
        port: quotation.port,
        locationId: quotation.locationId,
        inspectionTypeId: quotation.inspectionTypeId,
        code: quotation.CountryCode,
        location: quotation.port + ", " + quotation.country,
        quotationDate:
            getInspectionDate(
                quotation.quotationDateFrom.toString(),
                quotation.quotationDateTo?.toString()
            ),
        quotationDateSortField: quotation.quotationDateFrom,
        quotationDateFilterField: {
            dateFrom: quotation.quotationDateFrom.toString(),
            dateTo: quotation.quotationDateTo?.toString()
        },
        statusDate: getInspectionDate(quotation.statusDate.toString(), undefined),
        statusDateSortField: quotation.statusDate,
        id: quotation.id
    })) : [];

    const distinctInspectionTypes: string[] = Array.from(new Set(tableRows.map((value) => value.inspectionType)));
    const distinctVesselTypes: string[] = Array.from(new Set(tableRows.map((value) => value.vesselType)));
    const distinctQuotationStatuses: string[] = Array.from(new Set(tableRows.map((value) => value.statusName)));

    const navigateToQuotationDetails = (rowData: any) => {
        const quotationId = rowData.id;
        navigate(`/${QUOTATIONS_ROUTE}/${quotationId}`);
    }

    const resetTable = () => {
        sessionStorage.removeItem(ENQUIRIES_TABLE_KEY);
        // @ts-ignore due to type difficulties with ref.current.reset - I don't think it's an important enough type to spend too long researching
        enquiriesTableRef.current?.reset();
        onFilterFunction({filters: defaultEnquiriesFiltersConfig()});
    };

    const onFilterFunction = (e: any) => {
        setFilters(e.filters);
        setDebouncedSearchValue(e.filters.global.value)
    }

    const downloadConfirmationQuotation = async (rowData: QuotationEnquiriesTableRow) => {
        successToast(t("report.downloadStarted"));
        const baseUrl = "https://" + process.env.REACT_APP_ASIMS_ENDPOINT;
        const pdfUrl = baseUrl + "/public/Quotation/Pdf/" + rowData.id.toLowerCase();
        const downloadName =  rowData.vesselName + '_ENQUIRY.pdf'; // Set the name for the downloaded file;

        downloadFile(pdfUrl, downloadName, t);
    }

    const isloading = QuotationsStatus === "pending" || QuotationsStatus === "idle";

    return (
        <div className="table-container home-table" data-c="quotation-enquiries-table">
            <DataTable
                ref={enquiriesTableRef}
                emptyMessage={t("quotations.enquiries.table.emptyTable")}
                removableSort
                stripedRows
                paginator
                scrollable
                first={first}
                rows={rows}
                onPage={onPage}
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                scrollHeight="55vh"
                filters={filters}
                filterDisplay="row"
                stateStorage="session"
                stateKey={ENQUIRIES_TABLE_KEY}
                onFilter={onFilterFunction}
                sortField={QuotationEnquiriesTableFields.STATUS_DATE_SORT_FIELD}
                sortOrder={-1}
                value={tableRows}
                loading={isloading}
                globalFilterFields={enquiriesTableGlobalFilterFields}
                // @ts-ignore due to type difficulties with ref.current.reset - I don't think it's an important enough type to spend too long researching
                header={TableHeader({setGlobalFilterValue, onGlobalFilterChange, globalFilterValue: debouncedSearchValue, resetTable})}
            >
                {vesselColumn(
                    QuotationEnquiriesTableFields.VESSEL,
                    (data: any) => vesselBodyTemplate(data, () => {}, false)
                )}
                <Column
                    field={InspectionsTableFields.ACTIONS}
                    header={t("inspections.table.actions")}
                    body={(data: QuotationEnquiriesTableRow) =>
                        QuoteTableActionsBody(
                            data,
                            (data: any) => navigate(`/quotations/${data.id}`),
                            t,
                            downloadConfirmationQuotation
                        )
                    }
                />
                <Column
                    field={QuotationEnquiriesTableFields.TOTAL_PRICE_SORT_FIELD}
                    header={t("quotations.enquiries.table.priceHeader")}
                    body={
                        (data: any) => data.totalPrice
                    }
                    sortable
                />
                <Column
                    field={QuotationEnquiriesTableFields.INSPECTION_TYPE}
                    header={t("quotations.enquiries.table.inspectionTypeHeader")}
                    body={(data: any) => RowWithSubtext(
                        data,
                        QuotationEnquiriesTableFields.INSPECTION_TYPE,
                        QuotationEnquiriesTableFields.QUOTATION_NUMBER)
                    }
                    sortable
                    filter
                    showFilterMenu={false}
                    filterElement={(options) => MultiSelectTemplate(
                        options,
                        distinctInspectionTypes,
                        t("inspections.table.showAll"),
                        "inspection-type-filter-enquiries"
                    )}
                />
                <Column
                    field={QuotationEnquiriesTableFields.STATUS_DATE_SORT_FIELD}
                    header={t("quotations.enquiries.table.statusHeader")}
                    body={(data: any) => RowWithSubtext(
                        data,
                        QuotationEnquiriesTableFields.STATUS_FRIENDLY_NAME,
                        QuotationEnquiriesTableFields.STATUS_DATE)
                    }
                    sortable
                    filter
                    filterField={QuotationEnquiriesTableFields.STATUS_FRIENDLY_NAME}
                    showFilterMenu={false}
                    filterElement={(options) => MultiSelectTemplate(
                        options,
                        distinctQuotationStatuses,
                        t("inspections.table.showAll"),
                        "vessel-type-filter-enquiries"
                    )}
                />
                {vesselTypeColumn(
                    QuotationEnquiriesTableFields.VESSEL_TYPE,
                    distinctVesselTypes)
                }
                {inspectionDateColumn(
                    QuotationEnquiriesTableFields.QUOTATION_DATE_SORT_FIELD,
                    QuotationEnquiriesTableFields.QUOTATION_DATE_FILTER_FIELD,
                    (data: QuotationEnquiriesTableRow) =>
                        RowWithSubtext(
                            data,
                            QuotationEnquiriesTableFields.QUOTATION_DATE,
                            QuotationEnquiriesTableFields.LOCATION
                        ),
                    quotationDateFilterOptions
                )}
            </DataTable>
        </div>
    )
}
